import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Spinner,
  Button,
  Flex,
  Divider,
  Badge,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { AudioPlayer } from '../components/AudioPlayer';
import { NowPlaying } from '../components/NowPlaying';
import { LocationSelector } from '../components/LocationSelector';
import { PlaybackHistory } from '../components/PlaybackHistory';
import { StreamLinkModal } from '../components/StreamLinkModal';
import { AnnouncementModal } from '../components/AnnouncementModal';
import { getPlayableLocations, getStreamInfo, type PlayableLocationDto, type StreamInfoDto } from '../api/streamApi';
import {
  getNowPlaying,
  getQueue,
  skipTrack,
  previousTrack,
  type NowPlayingDto,
  type LocationQueueStateDto,
} from '../api/queueApi';
import { useSubscription } from '../../subscription/hooks/useSubscription';

export function PlayerPage() {
  const { t } = useTranslation();
  const { locationId } = useParams<{ locationId?: string }>();
  const navigate = useNavigate();
  const { isLocked } = useSubscription();
  const streamLinkModal = useDisclosure();
  const announcementModal = useDisclosure();

  const [locations, setLocations] = useState<PlayableLocationDto[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(locationId || null);
  const [streamInfo, setStreamInfo] = useState<StreamInfoDto | null>(null);
  const [nowPlaying, setNowPlaying] = useState<NowPlayingDto | null>(null);
  const [queue, setQueue] = useState<LocationQueueStateDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [controlBusy, setControlBusy] = useState(false);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const locs = await getPlayableLocations();
        setLocations(locs);
        if (!selectedLocationId && locs.length > 0) setSelectedLocationId(locs[0].id);
      } catch {
        setError('player.errors.loadLocations');
      } finally {
        setIsLoading(false);
      }
    };
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedLocationId) return;
    const loadStream = async () => {
      try {
        setStreamInfo(await getStreamInfo(selectedLocationId));
        setError(null);
      } catch {
        setStreamInfo(null);
        setError('player.errors.loadStream');
      }
    };
    loadStream();
  }, [selectedLocationId]);

  const refreshNowPlaying = useCallback(async () => {
    if (!selectedLocationId) return;
    try {
      setNowPlaying(await getNowPlaying(selectedLocationId));
    } catch {
      setNowPlaying(null);
    }
  }, [selectedLocationId]);

  const refreshQueue = useCallback(async () => {
    if (!selectedLocationId) return;
    try {
      setQueue(await getQueue(selectedLocationId));
    } catch {
      setQueue(null);
    }
  }, [selectedLocationId]);

  useEffect(() => {
    if (!selectedLocationId) return;
    refreshNowPlaying();
    refreshQueue();
    const interval = setInterval(() => {
      refreshNowPlaying();
      refreshQueue();
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedLocationId, refreshNowPlaying, refreshQueue]);

  const handleLocationChange = (id: string) => {
    setSelectedLocationId(id);
    navigate(`/player/${id}`, { replace: true });
  };

  const runControl = async (fn: (id: string) => Promise<unknown>) => {
    if (!selectedLocationId || isLocked) return;
    setControlBusy(true);
    try {
      await fn(selectedLocationId);
      await refreshNowPlaying();
      await refreshQueue();
    } catch {
      /* surfaced via polling */
    } finally {
      setControlBusy(false);
    }
  };

  if (isLoading) {
    return (
      <VStack flex={1} justify="center" align="center" p={8}>
        <Spinner size="xl" color="brand.500" />
      </VStack>
    );
  }

  if (locations.length === 0) {
    return (
      <VStack flex={1} justify="center" align="center" p={8}>
        <Text color="gray.500">{t('player.noLocations')}</Text>
      </VStack>
    );
  }

  const selectedName = locations.find((l) => l.id === selectedLocationId)?.name ?? '';

  return (
    <Box maxW="600px" mx="auto" data-testid="player-page">
      <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
        <Heading size="md" color="gray.800">
          {t('player.title')}
        </Heading>
        {selectedLocationId && (
          <Button size="sm" variant="outline" onClick={streamLinkModal.onOpen}>
            {t('player.shareLink')}
          </Button>
        )}
      </Flex>

      {locations.length > 1 && (
        <Box mb={6}>
          <LocationSelector
            locations={locations}
            selectedLocationId={selectedLocationId}
            onSelect={handleLocationChange}
          />
        </Box>
      )}

      {isLocked && (
        <Box bg="orange.50" borderWidth="1px" borderColor="orange.200" borderRadius="md" px={4} py={3} mb={4}>
          <Text color="orange.700" fontSize="sm">
            {t('player.lockedNotice')}
          </Text>
        </Box>
      )}

      <AudioPlayer
        streamUrl={isLocked ? null : streamInfo?.streamUrl || null}
        trackTitle={nowPlaying?.currentTrack?.title}
        trackArtist={nowPlaying?.currentTrack?.artist || undefined}
      />

      {!isLocked && selectedLocationId && (
        <HStack justify="center" mt={3} spacing={3}>
          <Button size="sm" variant="outline" onClick={() => runControl(previousTrack)} isDisabled={controlBusy}>
            ⏮ {t('player.previous')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => runControl(skipTrack)} isDisabled={controlBusy}>
            {t('player.skip')} ⏭
          </Button>
          <Button size="sm" colorScheme="brand" variant="ghost" onClick={announcementModal.onOpen}>
            {t('player.addAnnouncement')}
          </Button>
        </HStack>
      )}

      <Box mt={6}>
        <NowPlaying nowPlaying={nowPlaying} />
      </Box>

      {error && (
        <Text color="red.500" mt={4} textAlign="center" fontSize="sm">
          {t(error)}
        </Text>
      )}

      {queue && queue.items.length > 0 && (
        <Box mt={8}>
          <Heading size="sm" color="gray.700" mb={2}>
            {t('player.queue')}
          </Heading>
          <VStack align="stretch" spacing={0} divider={<Divider />}>
            {queue.items.map((item) => (
              <HStack key={item.id} py={2} px={1} spacing={3} opacity={item.isPlayed ? 0.5 : 1}>
                <Text flex={1} minW={0} fontSize="sm" color="gray.800" noOfLines={1}>
                  {item.track.title}
                  {item.track.artist ? ` — ${item.track.artist}` : ''}
                </Text>
                {item.position === queue.currentPosition && (
                  <Badge colorScheme="green">{t('player.nowLabel')}</Badge>
                )}
              </HStack>
            ))}
          </VStack>
        </Box>
      )}

      {selectedLocationId && (
        <Box mt={8}>
          <PlaybackHistory locationId={selectedLocationId} />
        </Box>
      )}

      {selectedLocationId && (
        <>
          <StreamLinkModal
            isOpen={streamLinkModal.isOpen}
            onClose={streamLinkModal.onClose}
            locationId={selectedLocationId}
            locationName={selectedName}
          />
          <AnnouncementModal
            isOpen={announcementModal.isOpen}
            onClose={announcementModal.onClose}
            locationId={selectedLocationId}
            onEnqueued={() => {
              refreshNowPlaying();
              refreshQueue();
            }}
          />
        </>
      )}
    </Box>
  );
}
